import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'

import { apiClient } from '@/lib/api/client'
import { wsClient } from '@/lib/ws/client'
import {
  login as loginEndpoint,
  logout as logoutEndpoint,
  refresh as refreshEndpoint,
  signup as signupEndpoint,
} from '@/lib/api/endpoints/auth'
import { AuthContext, type AuthContextValue } from '@/lib/auth/auth-context'
import { decodeJwtPayload } from '@/lib/auth/jwt'
import {
  clearSession,
  readSession,
  writeSession,
} from '@/lib/auth/session-storage'
import type { AuthSession, User } from '@/types/api'

/**
 * Estados possíveis da sessão. Por que um discriminated union em vez de
 * `user | null + isLoading + error`: o consumidor jamais precisa lidar com
 * combinações inválidas (ex.: `loading=false, user=null, error=null` é
 * "anônimo", mas tipos soltos deixam confundir).
 */
type AuthState =
  | { status: 'hydrating' }
  | { status: 'anonymous' }
  | {
      status: 'authenticated'
      user: User
      /**
       * Mantemos o token reativo no STATE (não só no ref) para que o
       * `AuthDebugBadge` re-renderize a cada refresh. O ref continua
       * sendo a fonte da verdade pra `apiClient.installAuthHooks`.
       */
      accessToken: string
      expiresAt: string
    }

export interface AuthProviderProps {
  children: ReactNode
  /**
   * Para onde mandar o usuário quando o refresh falha em background (sessão
   * expirada). Default: `/login`. Recebe a rota como prop pra facilitar testes.
   */
  loginRoute?: string
}

export function AuthProvider({
  children,
  loginRoute = '/login',
}: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({ status: 'hydrating' })
  const navigate = useNavigate()
  /**
   * `sessionRef` espelha o session salvo no storage. Por que ref + state:
   * o `apiClient.installAuthHooks` precisa de um `refresh()` *estável* (sem
   * recriar a cada render), mas a função precisa ler o refresh token MAIS
   * RECENTE (que pode ter sido rotacionado). Ref dá a leitura fresh sem
   * invalidar o callback.
   */
  const sessionRef = useRef<AuthSession | null>(null)

  // -----------------------------------------------------------------------------
  // Hidratação inicial: lê storage → restaura sessão diretamente do storage.
  // O user já foi validado e gravado no login/refresh, não precisa de round-trip.
  // Se o access token estiver expirado, o interceptor do ApiClient fará o
  // refresh automaticamente na primeira chamada protegida.
  // -----------------------------------------------------------------------------
  useEffect(() => {
    const stored = readSession()
    if (!stored) {
      setState({ status: 'anonymous' })
      return
    }
    sessionRef.current = stored
    setState({
      status: 'authenticated',
      user: stored.user,
      accessToken: stored.accessToken,
      expiresAt: stored.expiresAt,
    })
  }, [])

  // -----------------------------------------------------------------------------
  // Plug dos hooks no apiClient. Estável, lê sessão fresh do ref.
  // -----------------------------------------------------------------------------
  useEffect(() => {
    apiClient.installAuthHooks({
      refresh: async () => {
        const current = sessionRef.current
        if (!current) return null
        try {
          const next = await refreshEndpoint({
            refreshToken: current.refreshToken,
          })
          sessionRef.current = next
          writeSession(next)
          // Se o estado estava em "anônimo" (raro: refresh em background sem
          // logout explícito), promove pra autenticado também. Sempre que
          // refresh acontece, o accessToken/expiresAt mudam — re-renderizar
          // pra que o badge de visualização reaja.
          setState({
            status: 'authenticated',
            user: next.user,
            accessToken: next.accessToken,
            expiresAt: next.expiresAt,
          })
          return next.accessToken
        } catch {
          return null
        }
      },
      onUnauthorized: () => {
        sessionRef.current = null
        clearSession()
        wsClient.disconnectAll()
        setState({ status: 'anonymous' })
        navigate(loginRoute, { replace: true })
      },
    })
    return () => {
      apiClient.uninstallAuthHooks()
    }
  }, [navigate, loginRoute])

  // -----------------------------------------------------------------------------
  // Ações expostas
  // -----------------------------------------------------------------------------
  const login = useCallback<AuthContextValue['login']>(async (input) => {
    const session = await loginEndpoint(input)
    sessionRef.current = session
    writeSession(session)
    setState({
      status: 'authenticated',
      user: session.user,
      accessToken: session.accessToken,
      expiresAt: session.expiresAt,
    })
  }, [])

  // Mesma mecânica do login: o backend retorna AuthSession completa pós-signup
  // (admin.create_user com email_confirm=true permite login imediato). Session
  // é gravada no storage e o redirect pra /dashboard fica a cargo da página.
  const signup = useCallback<AuthContextValue['signup']>(async (input) => {
    const session = await signupEndpoint(input)
    sessionRef.current = session
    writeSession(session)
    setState({
      status: 'authenticated',
      user: session.user,
      accessToken: session.accessToken,
      expiresAt: session.expiresAt,
    })
  }, [])

  /**
   * Refresh manual exposto para o `AuthDebugBadge`. Reusa o mesmo endpoint
   * que o interceptor do `apiClient` chama, mantendo a fonte da verdade
   * única. Atualiza state + ref + storage atomicamente.
   */
  const refresh = useCallback<AuthContextValue['refresh']>(async () => {
    const current = sessionRef.current
    if (!current) return false
    try {
      const next = await refreshEndpoint({ refreshToken: current.refreshToken })
      sessionRef.current = next
      writeSession(next)
      setState({
        status: 'authenticated',
        user: next.user,
        accessToken: next.accessToken,
        expiresAt: next.expiresAt,
      })
      return true
    } catch {
      return false
    }
  }, [])

  const logout = useCallback<AuthContextValue['logout']>(async () => {
    try {
      await logoutEndpoint()
    } catch {
      // best-effort — usuário pode estar offline ou refresh já expirou
    }
    sessionRef.current = null
    clearSession()
    wsClient.disconnectAll()
    setState({ status: 'anonymous' })
    navigate(loginRoute, { replace: true })
  }, [navigate, loginRoute])

  const value = useMemo<AuthContextValue>(() => {
    const isAuth = state.status === 'authenticated'
    const accessToken = isAuth ? state.accessToken : null
    return {
      isHydrating: state.status === 'hydrating',
      isAuthenticated: isAuth,
      user: isAuth ? state.user : null,
      accessToken,
      expiresAt: isAuth ? state.expiresAt : null,
      // `decodeJwtPayload` é puro e barato — não vale memoizar aparte
      // (o `useMemo` já recalcula só quando `state` muda).
      claims: decodeJwtPayload(accessToken),
      login,
      signup,
      logout,
      refresh,
    }
  }, [state, login, signup, logout, refresh])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
